// Port of the ImportCSV WinForms tool's conversion logic (CsvFile / MappingProfile /
// RowMapper / ConversionOptions). Pure, framework-free helpers so the dialog can
// parse an arbitrary source CSV, map its columns onto the import target schema,
// preview the result and emit a ready-to-validate CSV File.

/** Target fields fed from a source column (the rest come from constant options). */
export const TARGET_FIELDS = [
  'Number',
  'DateOfBirth',
  'Genetics',
  'Cycle',
  'TattooNumber',
  'PlasticNumber',
  'Status'
] as const;
export type TargetField = (typeof TARGET_FIELDS)[number];

/** Output schema. Column order here drives the generated CSV column order. */
export const TARGET_COLUMNS = [
  'Number',
  'AnimalGender',
  'AnimalType',
  'DateOfBirth',
  'Genetics',
  'WeightCategory',
  'Status',
  'Cycle',
  'TattooNumber',
  'PlasticNumber',
  'FarmNumber',
  'Notes'
] as const;

/** A source column header for each target field, or null to leave it empty. */
export type MappingProfile = Record<TargetField, string | null>;

/** Constant values applied to every row. */
export interface ConversionOptions {
  farmNumber: string;
  animalType: string;
  animalGender: string;
  weightCategory: string;
  notes: string;
}

export type TargetRow = Record<(typeof TARGET_COLUMNS)[number], string>;

export interface ParsedCsv {
  headers: string[];
  rows: Array<Record<string, string>>;
}

export const ANIMAL_TYPE_OPTIONS = ['Sow', 'Boar', 'Gilt', 'Piglet'];
export const GENDER_OPTIONS = ['Female', 'Male'];
export const WEIGHT_OPTIONS = ['Light', 'Medium', 'Heavy'];

/** Mirrors ConversionOptions defaults from the WinForms tool. */
export const DEFAULT_OPTIONS: ConversionOptions = {
  farmNumber: 'FRM-001',
  animalType: 'Sow',
  animalGender: 'Female',
  weightCategory: 'Medium',
  notes: ''
};

/**
 * Best-effort default mapping for the Dutch herd-management export. Each guess
 * only sticks if a matching header actually exists in the file (case-insensitive).
 */
export function createDefaultMapping(headers: string[]): MappingProfile {
  const present = new Map(headers.map((h) => [h.toLowerCase(), h]));
  const pick = (...names: string[]): string | null => {
    for (const name of names) {
      const match = present.get(name.toLowerCase());
      if (match) return match;
    }
    return null;
  };

  return {
    Number: pick('Plast. Nr.', 'Plast. Nr', 'Number'),
    DateOfBirth: pick('Geboorte datum', 'DateOfBirth', 'Date of birth'),
    Genetics: pick('Lijn', 'Genetics', 'Line'),
    Cycle: pick('Cyclus', 'Cycle'),
    TattooNumber: pick('Tat.nr.', 'TattooNumber', 'Tattoo'),
    PlasticNumber: pick('Plast. Nr.', 'Plast. Nr', 'PlasticNumber', 'Plastic'),
    Status: null
  };
}

const DELIMITERS = [',', ';', '\t', '|'];

function detectDelimiter(headerLine: string): string {
  let best = ',';
  let bestCount = -1;
  for (const candidate of DELIMITERS) {
    const count = headerLine.split(candidate).length - 1;
    if (count > bestCount) {
      bestCount = count;
      best = candidate;
    }
  }
  return best;
}

/** RFC-4180-ish record splitter that honours quoted fields and embedded newlines. */
function splitRecords(content: string, delimiter: string): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  const endField = () => {
    record.push(field);
    field = '';
  };
  const endRecord = () => {
    endField();
    records.push(record);
    record = [];
  };

  while (i < content.length) {
    const ch = content[i];

    if (inQuotes) {
      if (ch === '"') {
        if (content[i + 1] === '"') {
          field += '"';
          i += 2;
        } else {
          inQuotes = false;
          i += 1;
        }
      } else {
        field += ch;
        i += 1;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i += 1;
    } else if (ch === delimiter) {
      endField();
      i += 1;
    } else if (ch === '\n') {
      endRecord();
      i += 1;
    } else if (ch === '\r') {
      endRecord();
      i += content[i + 1] === '\n' ? 2 : 1;
    } else {
      field += ch;
      i += 1;
    }
  }

  // Flush the trailing field/record if the file did not end with a newline.
  if (field !== '' || record.length > 0) endRecord();

  return records;
}

/** Parse CSV text into headers + header-keyed rows (delimiter auto-detected). */
export function parseCsv(text: string): ParsedCsv {
  const content = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text; // strip BOM
  if (content.trim().length === 0) return { headers: [], rows: [] };

  const firstBreak = content.search(/\r?\n/);
  const headerLine = firstBreak === -1 ? content : content.slice(0, firstBreak);
  const delimiter = detectDelimiter(headerLine);

  const records = splitRecords(content, delimiter);
  if (records.length === 0) return { headers: [], rows: [] };

  const headers = records[0].map((h) => h.trim());
  const rows = records
    .slice(1)
    .filter((cells) => cells.some((c) => c.trim() !== '')) // drop fully blank lines
    .map((cells) => {
      const row: Record<string, string> = {};
      headers.forEach((header, idx) => {
        row[header] = (cells[idx] ?? '').trim();
      });
      return row;
    });

  return { headers, rows };
}

// Dutch source dates are day-first: "1-5-2025", "30-10-2025", "01/05/2025".
function parseAndFormatDate(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';

  const match = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (!match) throw new Error(`Unrecognized date: '${raw}'`);

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
    throw new Error(`Unrecognized date: '${raw}'`);
  }

  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/** Map a single source row to a target row. Throws on an unrecognised date. */
export function mapRow(row: Record<string, string>, mapping: MappingProfile, options: ConversionOptions): TargetRow {
  const src = (header: string | null): string => (header && header in row ? row[header] : '');

  const cycleRaw = src(mapping.Cycle).trim();
  const cycle = /^-?\d+$/.test(cycleRaw) ? cycleRaw : '0';

  return {
    Number: src(mapping.Number).trim(),
    AnimalGender: options.animalGender,
    AnimalType: options.animalType,
    DateOfBirth: parseAndFormatDate(src(mapping.DateOfBirth)),
    Genetics: src(mapping.Genetics),
    WeightCategory: options.weightCategory,
    Status: src(mapping.Status),
    Cycle: cycle,
    TattooNumber: src(mapping.TattooNumber).trim(),
    PlasticNumber: src(mapping.PlasticNumber).trim(),
    FarmNumber: options.farmNumber,
    Notes: options.notes
  };
}

export interface MapResult {
  rows: TargetRow[];
  skipped: number;
  /** First mapping error encountered (row number + message), for status display. */
  firstError: string;
}

/** Map every parsed row, collecting successes and skipping rows that fail. */
export function mapAll(parsed: ParsedCsv, mapping: MappingProfile, options: ConversionOptions): MapResult {
  const rows: TargetRow[] = [];
  let skipped = 0;
  let firstError = '';
  let rowNumber = 1; // header is row 1 in user-facing terms

  for (const row of parsed.rows) {
    rowNumber += 1;
    try {
      rows.push(mapRow(row, mapping, options));
    } catch (e) {
      skipped += 1;
      if (firstError === '') firstError = `row ${rowNumber}: ${(e as Error).message}`;
    }
  }

  return { rows, skipped, firstError };
}

function escapeCsvValue(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Serialise mapped rows to a CSV string using the target schema column order. */
export function buildCsv(rows: TargetRow[]): string {
  const header = TARGET_COLUMNS.join(',');
  const lines = rows.map((row) => TARGET_COLUMNS.map((col) => escapeCsvValue(row[col] ?? '')).join(','));
  return [header, ...lines].join('\r\n');
}
